import React, { useState, useEffect, useRef } from 'react';
import { aiAgentAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Bot,
  Send,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle,
  MessageSquare,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AIAgentChat({ compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSummary = async () => {
    try {
      const res = await aiAgentAPI.getSummary();
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await aiAgentAPI.chat(userMessage, conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setConversationId(res.data.conversation_id);
    } catch (error) {
      toast.error('Erreur de communication avec l\'agent IA');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = [
    { label: 'Qui relancer ?', prompt: 'Quels clients dois-je relancer en priorité aujourd\'hui ?' },
    { label: 'Mes devis urgents', prompt: 'Quels sont mes devis les plus urgents à traiter ?' },
    { label: 'Conseil de vente', prompt: 'Donne-moi un conseil pour améliorer mon taux de conversion.' },
    { label: 'Résumé du jour', prompt: 'Fais-moi un résumé de mon activité du jour.' },
  ];

  const handleQuickAction = async (prompt) => {
    if (loading) return;
    
    setMessages(prev => [...prev, { role: 'user', content: prompt }]);
    setLoading(true);

    try {
      const res = await aiAgentAPI.chat(prompt, conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setConversationId(res.data.conversation_id);
    } catch (error) {
      toast.error('Erreur de communication avec l\'agent IA');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (compact && !expanded) {
    return (
      <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50">
        <CardContent className="p-4">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setExpanded(true)}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Assistant IA Commercial</h3>
                <p className="text-sm text-slate-500">Cliquez pour ouvrir le chat</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {summary && (
                <>
                  {summary.counts.critical_3_weeks > 0 && (
                    <Badge variant="destructive" className="animate-pulse">
                      {summary.counts.critical_3_weeks} urgent
                    </Badge>
                  )}
                  {summary.counts.alert_2_weeks > 0 && (
                    <Badge variant="outline" className="border-orange-300 text-orange-600">
                      {summary.counts.alert_2_weeks} alerte
                    </Badge>
                  )}
                </>
              )}
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 via-white to-amber-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Assistant IA Commercial
                <Sparkles className="w-4 h-4 text-yellow-500" />
              </CardTitle>
              <p className="text-xs text-slate-500">Propulsé par Claude Sonnet 4.5</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchSummary}
              className="text-slate-500"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {compact && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setExpanded(false)}
              >
                <ChevronUp className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-green-50 rounded-lg p-2 text-center border border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-green-700">{summary.counts.today}</p>
              <p className="text-[10px] text-green-600">Aujourd'hui</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 text-center border border-blue-200">
              <Clock className="w-4 h-4 text-blue-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-blue-700">{summary.counts.waiting_1_week}</p>
              <p className="text-[10px] text-blue-600">1 semaine</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-2 text-center border border-orange-200">
              <AlertCircle className="w-4 h-4 text-orange-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-orange-700">{summary.counts.alert_2_weeks}</p>
              <p className="text-[10px] text-orange-600">2 semaines</p>
            </div>
            <div className="bg-red-50 rounded-lg p-2 text-center border border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-red-700">{summary.counts.critical_3_weeks}</p>
              <p className="text-[10px] text-red-600">3+ semaines</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction(action.prompt)}
              disabled={loading}
              className="text-xs bg-white hover:bg-yellow-50 border-yellow-200"
            >
              {action.label}
            </Button>
          ))}
        </div>

        {/* Chat Messages */}
        <ScrollArea className="h-64 rounded-lg border bg-white p-3">
          {messages.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Posez une question à votre assistant IA</p>
              <p className="text-xs mt-1">Ex: "Qui dois-je relancer aujourd'hui ?"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "flex",
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                      msg.role === 'user'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-slate-100 text-slate-800'
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <Bot className="w-3 h-3 inline-block mr-1 mb-0.5" />
                    )}
                    <span className="whitespace-pre-wrap">{msg.content}</span>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-lg px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Posez votre question..."
            disabled={loading}
            className="flex-1"
          />
          <Button 
            onClick={handleSend} 
            disabled={loading || !input.trim()}
            className="bg-yellow-500 hover:bg-yellow-600"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
